import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { friendlyError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import ConfirmAction from "@/components/ConfirmAction";
import { Card, Chip, Button, Skeleton, EmptyState, Toast } from "@/ui";
import { useNetwork } from "@/context/NetworkContext";
import { manilaDateTime } from "@/lib/format";
import { AlertTriangle, Inbox, WifiOff } from "lucide-react";

const STATUS_TONE = { paid: "good", done: "good", ready: "good", preparing: "warn", awaiting_payment: "warn", cancelled: "bad", refunded: "bad" };
const CACHE_KEY = "reto_today_cache";

export default function Today() {
  const { shopId, shopName, user } = useAuth();
  const { online } = useNetwork();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [staleAt, setStaleAt] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [confirmError, setConfirmError] = useState("");
  const [notice, setNotice] = useState("");

  const load = useCallback(() => {
    if (!shopId) return;
    setLoading(true);
    api.get(`/today?shopId=${shopId}`).then((r) => {
      setData(r.data); setStaleAt(null); setError("");
      try { localStorage.setItem(CACHE_KEY, JSON.stringify({ shopId, at: Date.now(), data: r.data })); } catch {}
    }).catch((e) => {
      // Offline / failed: show the last figures we saved, with when they were from.
      try {
        const cached = JSON.parse(localStorage.getItem(CACHE_KEY) || "null");
        if (cached && cached.shopId === shopId) { setData(cached.data); setStaleAt(cached.at); }
        else setError(friendlyError(e));
      } catch { setError(friendlyError(e)); }
    }).finally(() => setLoading(false));
  }, [shopId]);

  useEffect(() => { if (!shopId) { navigate("/pick-shop", { replace: true }); return; } load(); }, [shopId, load, navigate]);

  const grant = async () => {
    setConfirmBusy(true); setConfirmError("");
    try { const r = await api.post("/grants/message-access", { shopId, granteeRole: "helper" }); setNotice(r.data.message); setConfirmOpen(false); }
    catch (e) { setConfirmError(friendlyError(e)); } finally { setConfirmBusy(false); }
  };

  return (
    <Layout shopName={shopName}>
      <h1 className="t-h1" style={{ marginBottom: 24 }}>today</h1>
      {(!online || staleAt) ? (
        <div className="offline-banner" data-testid="today-offline">
          <WifiOff size={20} strokeWidth={1.75} />
          <div>
            <div className="t-body">{online ? "showing your last saved numbers" : "you're offline \u2014 showing your last saved numbers"}</div>
            {staleAt ? <div className="muted-3 t-small">last updated {manilaDateTime(new Date(staleAt).toISOString())}</div> : null}
          </div>
        </div>
      ) : null}
      {error ? <div className="err-text" data-testid="today-error" style={{ marginBottom: 12 }}>{error}</div> : null}

      {loading ? (
        <div className="grid grid-3" style={{ marginBottom: 32 }}><Skeleton variant="card" /><Skeleton variant="card" /><Skeleton variant="card" /></div>
      ) : data ? (
        <>
          <div className="grid grid-3 section">
            {data.canSeeTakings ? (
              <Card selected data-testid="takings-card">
                <p className="t-label">money taken today</p>
                <div className="t-display num" data-testid="takings-value" style={{ marginTop: 8 }}>{data.takingsToday}</div>
                <p className="t-small" style={{ marginTop: 8 }}>only you and the platform owner see this</p>
              </Card>
            ) : null}
            <Card>
              <p className="t-label muted">orders today</p>
              <div className="t-display num" data-testid="orders-today" style={{ marginTop: 8 }}>{data.orderCountToday}</div>
              <Link to="/orders" className="btn btn-sm" style={{ marginTop: 12, display: "inline-flex" }}>see all orders</Link>
            </Card>
          </div>

          <div className="section" data-testid="needs-attention">
            <h2 className="t-h2" style={{ marginBottom: 12 }}>what needs looking at</h2>
            {data.needsAttention && data.needsAttention.length > 0 ? (
              <div className="stack">
                {data.needsAttention.map((n, i) => (
                  <Card key={i}><div className="row" style={{ alignItems: "flex-start" }}>
                    <span style={{ color: "var(--warn)" }}><AlertTriangle size={20} strokeWidth={1.75} /></span>
                    <span className="t-body">{n.message}</span>
                  </div></Card>
                ))}
              </div>
            ) : <EmptyState icon={<Inbox size={20} strokeWidth={1.75} />} title="nothing needs looking at right now — you are all caught up" />}
          </div>

          <div className="section">
            <h2 className="t-h2" style={{ marginBottom: 12 }}>latest orders today</h2>
            {data.recentOrders && data.recentOrders.length > 0 ? (
              <div className="stack">
                {data.recentOrders.map((o) => (
                  <Card key={o.id} interactive onClick={() => navigate(`/orders/${o.id}`)}>
                    <div className="row-between">
                      <span className="t-h3 num">{o.gross}</span>
                      <Chip>{o.channelLabel}</Chip>
                    </div>
                    <div className="row" style={{ marginTop: 8 }}>
                      <Chip tone={STATUS_TONE[o.status] || "neutral"} dot>{o.statusLabel}</Chip>
                      <span className="muted-3 t-small">{o.itemCount} item(s) · {manilaDateTime(o.placedAt)}</span>
                    </div>
                  </Card>
                ))}
              </div>
            ) : <EmptyState icon={<Inbox size={20} strokeWidth={1.75} />} title="no orders yet today — new orders show up here as they come in" />}
          </div>

          {user?.role === "shop_owner" ? (
            <Card>
              <h3 className="t-h3">help from support</h3>
              <p className="muted t-small" style={{ margin: "4px 0 12px" }}>let a support helper read your messages for a day if you need help.</p>
              <Button data-testid="grant-access" onClick={() => { setConfirmError(""); setConfirmOpen(true); }}>give a helper access to messages</Button>
            </Card>
          ) : null}
        </>
      ) : null}

      {notice ? <div className="toast-wrap"><Toast tone="good" data-testid="today-notice">{notice}</Toast></div> : null}

      <ConfirmAction open={confirmOpen} title="give a helper access to your messages?"
        preview={["a support helper will be able to read your messages to help you.", "this turns off by itself after 24 hours.", "you can ask them to stop at any time."]}
        confirmLabel="yes, switch it on" busy={confirmBusy} error={confirmError} onConfirm={grant} onCancel={() => setConfirmOpen(false)} />
    </Layout>
  );
}
